/**
 * Swipe da bordo sinistro → home.
 * Solo edge stretto; non cattura i tap sui controlli ( +, menu, input ).
 */

import { clearAppLayoutInlineStyles } from './layout.ts'

export interface SwipeBackOptions {
  edgePx?: number
  thresholdPx?: number
  ignoreSelector?: string
}

export interface SwipeBackHandle {
  dispose: () => void
}

const INTERACTIVE =
  'button, a, input, textarea, select, label, [role="button"], .icon-btn, .attach-toggle-btn, .composer-attach-panel, .voice-mic-btn, #btn-voice, #btn-send, #btn-attach-toggle, #msg-input'

function isBlockingOverlayOpen(): boolean {
  return !!document.querySelector(
    '.modal-overlay:not([hidden]), .action-sheet-overlay:not([hidden]), #chat-menu-sheet:not([hidden]), .composer-attach-panel:not([hidden])',
  )
}

export function wireSwipeBack(
  element: HTMLElement,
  onBack: () => void,
  options: SwipeBackOptions = {},
): SwipeBackHandle {
  // Solo bordo fisico stretto — mai 30% dello schermo
  const edgePx = options.edgePx ?? 22
  const thresholdPx = options.thresholdPx ?? 80
  const ignoreSelector = options.ignoreSelector
    ? `${INTERACTIVE}, ${options.ignoreSelector}`
    : INTERACTIVE

  let tracking = false
  let armed = false
  let startX = 0
  let startY = 0
  let dragX = 0
  let activePointerId: number | null = null
  let touchActive = false

  const resetTransform = () => {
    element.classList.remove('is-swipe-back')
    element.style.removeProperty('transform')
    element.style.removeProperty('transition')
    if (element.id === 'app') clearAppLayoutInlineStyles(element)
  }

  const cancel = () => {
    tracking = false
    armed = false
    dragX = 0
    activePointerId = null
    touchActive = false
    resetTransform()
  }

  const onPointerDown = (e: PointerEvent) => {
    if (touchActive) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    if (tracking || isBlockingOverlayOpen()) return
    const target = e.target as Element | null
    if (target?.closest(ignoreSelector)) return

    const rect = element.getBoundingClientRect()
    if (e.clientX < rect.left || e.clientX > rect.right) return
    if (e.clientY < rect.top || e.clientY > rect.bottom) return
    // Solo primi ~22px dal bordo SINISTRO del pannello
    if (e.clientX - rect.left > edgePx) return

    // Armato ma NON cattura ancora — lascia passare i click fino allo swipe reale
    tracking = true
    armed = false
    startX = e.clientX
    startY = e.clientY
    dragX = 0
    activePointerId = e.pointerId
  }

  const onPointerMove = (e: PointerEvent) => {
    if (touchActive) return
    if (!tracking || activePointerId !== e.pointerId) return
    const dx = e.clientX - startX
    const dy = e.clientY - startY

    if (!armed) {
      // Gesture verticale / tap → abbandona subito, non interferire
      if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
        cancel()
        return
      }
      if (dx < 18) return
      // Swipe orizzontale confermato
      armed = true
      element.classList.add('is-swipe-back')
      try {
        element.setPointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
    }

    if (dx <= 0) return
    dragX = dx
    element.style.transition = 'none'
    element.style.transform = `translateX(${Math.min(dx, 120)}px)`
    e.preventDefault()
  }

  const onPointerEnd = (e: PointerEvent) => {
    if (touchActive) return
    if (!tracking || (activePointerId != null && activePointerId !== e.pointerId)) return
    const wasArmed = armed
    const finalDx = dragX
    try {
      if (wasArmed) element.releasePointerCapture(e.pointerId)
    } catch {
      /* ignore */
    }
    tracking = false
    armed = false
    activePointerId = null
    dragX = 0

    if (wasArmed && finalDx >= thresholdPx) {
      element.style.transition = 'transform 0.16s ease-out'
      element.style.transform = 'translateX(100%)'
      window.setTimeout(() => {
        resetTransform()
        onBack()
      }, 150)
    } else {
      if (wasArmed) {
        element.style.transition = 'transform 0.18s ease-out'
        element.style.transform = ''
        window.setTimeout(resetTransform, 200)
      } else {
        resetTransform()
      }
    }
  }

  const onTouchStart = (e: TouchEvent) => {
    if (tracking || isBlockingOverlayOpen()) return
    if (e.touches.length !== 1) return
    const t = e.touches[0]!
    const target = e.target as Element | null
    if (target?.closest(ignoreSelector)) return
    const rect = element.getBoundingClientRect()
    if (t.clientX < rect.left || t.clientX > rect.right) return
    if (t.clientY < rect.top || t.clientY > rect.bottom) return
    if (t.clientX - rect.left > edgePx) return
    tracking = true
    touchActive = true
    armed = false
    startX = t.clientX
    startY = t.clientY
    dragX = 0
  }

  const onTouchMove = (e: TouchEvent) => {
    if (!tracking || !touchActive || e.touches.length !== 1) return
    const t = e.touches[0]!
    const dx = t.clientX - startX
    const dy = t.clientY - startY
    if (!armed) {
      if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
        cancel()
        return
      }
      if (dx < 18) return
      armed = true
      element.classList.add('is-swipe-back')
    }
    if (dx <= 0) return
    dragX = dx
    element.style.transition = 'none'
    element.style.transform = `translateX(${Math.min(dx, 120)}px)`
    e.preventDefault()
  }

  const onTouchEnd = () => {
    if (!tracking || !touchActive) return
    const wasArmed = armed
    const finalDx = dragX
    tracking = false
    armed = false
    touchActive = false
    dragX = 0
    if (wasArmed && finalDx >= thresholdPx) {
      element.style.transition = 'transform 0.16s ease-out'
      element.style.transform = 'translateX(100%)'
      window.setTimeout(() => {
        resetTransform()
        onBack()
      }, 150)
    } else if (wasArmed) {
      element.style.transition = 'transform 0.18s ease-out'
      element.style.transform = ''
      window.setTimeout(resetTransform, 200)
    } else {
      resetTransform()
    }
  }

  // Solo sul pannello, non document (evita di rubare i click a tutto)
  element.addEventListener('pointerdown', onPointerDown)
  element.addEventListener('pointermove', onPointerMove)
  element.addEventListener('pointerup', onPointerEnd)
  element.addEventListener('pointercancel', onPointerEnd)
  element.addEventListener('touchstart', onTouchStart, { passive: true })
  element.addEventListener('touchmove', onTouchMove, { passive: false })
  element.addEventListener('touchend', onTouchEnd)
  element.addEventListener('touchcancel', onTouchEnd)
  element.classList.add('has-swipe-back')

  return {
    dispose: () => {
      element.removeEventListener('pointerdown', onPointerDown)
      element.removeEventListener('pointermove', onPointerMove)
      element.removeEventListener('pointerup', onPointerEnd)
      element.removeEventListener('pointercancel', onPointerEnd)
      element.removeEventListener('touchstart', onTouchStart)
      element.removeEventListener('touchmove', onTouchMove)
      element.removeEventListener('touchend', onTouchEnd)
      element.removeEventListener('touchcancel', onTouchEnd)
      element.classList.remove('has-swipe-back', 'is-swipe-back')
      cancel()
    },
  }
}
